import type { QueryClient } from "@tanstack/react-query";
import { redirect } from "@tanstack/react-router";
import {
	ORG_VISIBILITY_RULES_QUERY_KEY,
	VISIBILITY_RULES_QUERY_KEY,
} from "@/features/visibility/use-visibility-query";
import {
	adminApi,
	authApi,
	ensureImpersonation,
	getAccessToken,
	getCurrentUser,
	getTenantOverride,
	isEmbedded,
	refreshSession,
	setCurrentUser,
} from "@/lib/auth-api";
import { getSidebarRegisteredElementIds } from "@/lib/sidebar-config";
import type { VisibilityRule } from "@/lib/visibility-types";

const ADMIN_ROLES = new Set(["admin", "super_admin"]);
const SUPER_ADMIN_ROLES = new Set(["super_admin"]);
// Wyrownane do backendu: wszystkie endpointy /api/admin/* wymagaja admin lub
// super_admin (require_admin w backend/shared/src/routes/admin.rs:267). Role
// regional_manager/franchise_owner dostawaly 403 na kazdym fetchu strony
// /konfiguracja/uzytkownicy — guard puszczal, queries failowaly, Sentry spam.
const USER_MGMT_ROLES = new Set(["admin", "super_admin"]);

const VISIBILITY_STALE_MS = 5 * 60 * 1000;

// Static set of sidebar-registered element ids, lazily built once per tab. Matches
// exactly what <Visible> wrappers register at render time — used by the guard's
// ancestor check to replicate VisibilityProvider.isParentVisible outside React.
let cachedSidebarRegistered: Set<string> | null = null;
function sidebarRegisteredElements(): Set<string> {
	if (cachedSidebarRegistered === null) {
		cachedSidebarRegistered = getSidebarRegisteredElementIds();
	}
	return cachedSidebarRegistered;
}

/**
 * Replicates VisibilityProvider.isParentVisible (visibility-provider.tsx:35)
 * for route-guard semantics: every registered ancestor of `elementId` must
 * also be granted, otherwise hiding a parent module would leak via direct URL.
 *
 * Differences from the React version:
 *  - Uses a static `registered` set (sidebar-config derived) instead of the
 *    runtime registeredElements state, because beforeLoad runs outside React.
 *  - Assumes `elementId` starts with `sidebar.` — guards for other prefixes
 *    need to extend sidebarRegisteredElements() or pass their own set.
 */
function isParentGranted(elementId: string, grants: Set<string>, registered: Set<string>): boolean {
	const segments = elementId.split(".");
	if (segments.length <= 2) return true;
	const parent = segments.slice(0, -1).join(".");
	if (!registered.has(parent)) {
		return isParentGranted(parent, grants, registered);
	}
	if (!grants.has(parent)) return false;
	return isParentGranted(parent, grants, registered);
}

export async function ensureAuthenticated() {
	// Embedded (panel agenta): token wstrzyknięty, brak refresh-cookie w WebView —
	// nie próbuj /auth/refresh ani nie przekierowuj do /login.
	if (isEmbedded()) return;
	if (getAccessToken()) return;
	// refreshSession = silentRefresh + odtworzenie podgladu. Mutex w srodku sprawia,
	// ze jesli AuthProvider.bootstrap juz leci, dolaczamy do tego samego Promise
	// zamiast robic drugi /auth/refresh (ktory invalidowalby single-use refresh cookie).
	const refreshed = await refreshSession();
	if (!refreshed) {
		throw redirect({ to: "/auth/login" });
	}
}

async function loadUser() {
	let user = getCurrentUser();
	if (!user) {
		// Pusty `_currentUser` = swieze zaladowanie strony (wejscie z linka, F5). Jesli
		// sesja byla w trybie podgladu, aktualny token nalezy do OPERATORA, wiec
		// `/auth/me` zwrocilby TERAZ jego role i kazdy guard rolowy nizej podjalby
		// decyzje za niewlasciwego uzytkownika. Domykamy podglad, zanim ktokolwiek
		// zapyta o role — `ensureImpersonation` jest wspoldzielone z AuthProviderem
		// (mutex), wiec rownolegly bootstrap nie startuje drugiej impersonacji.
		await ensureImpersonation();
		try {
			user = await authApi.getMe();
			setCurrentUser(user);
		} catch {
			throw redirect({ to: "/auth/login" });
		}
	}
	return user;
}

export async function ensureAdminAccess() {
	await ensureAuthenticated();
	const user = await loadUser();
	if (!user.roles.some((r) => ADMIN_ROLES.has(r))) throw redirect({ to: "/dashboard" });
}

export async function ensureSuperAdminAccess() {
	await ensureAuthenticated();
	const user = await loadUser();
	if (!user.roles.some((r) => SUPER_ADMIN_ROLES.has(r))) throw redirect({ to: "/dashboard" });
}

export async function ensureUserManagementAccess() {
	await ensureAuthenticated();
	const user = await loadUser();
	if (!user.roles.some((r) => USER_MGMT_ROLES.has(r))) throw redirect({ to: "/dashboard" });
}

/**
 * Computes the set of element_ids granted to the current user, mirroring the union logic
 * from VisibilityProvider.userUnionGrants (visibility-provider.tsx:108). Kept as a pure
 * function so the unit tests can pin the semantics without mounting the provider.
 *
 * **Precondition**: `rules` is assumed to be pre-filtered to the caller's roles (what
 * `/visibility-rules/me` returns — backend does `WHERE role = ANY(user.roles)`), and
 * `orgRules` is pre-filtered to the caller's org groups. We do NOT re-filter by roles
 * here — if the backend contract ever changes to return unfiltered rules, the guard
 * would silently over-grant, so any refactor of `/visibility-rules/me` must also
 * revisit this function.
 *
 * Allow-over-deny semantics: within a single role, a later `visible: false` removes an
 * earlier grant. Across roles / org groups the result is a UNION — a grant from any
 * source wins. This is counterintuitive for RBAC (typical expectation: deny > allow)
 * but is exactly what VisibilityProvider does, so the guard mirrors it.
 */
export function computeGrantedElements(
	rules: VisibilityRule[],
	orgRules: readonly {
		groupId: number;
		elementId: string;
		visible: boolean;
	}[],
): Set<string> {
	// Per-role intermediate map — intentionally kept as a separate structure (rather
	// than a single pass) so the shape matches VisibilityProvider line-for-line; any
	// future audit that compares the two can see the parallel.
	const perRole = new Map<string, Set<string>>();
	for (const r of rules) {
		const set = perRole.get(r.role) ?? new Set<string>();
		if (r.visible) set.add(r.elementId);
		else set.delete(r.elementId);
		perRole.set(r.role, set);
	}
	const grants = new Set<string>();
	for (const set of perRole.values()) {
		for (const el of set) grants.add(el);
	}
	const perGroup = new Map<number, Set<string>>();
	for (const r of orgRules) {
		const set = perGroup.get(r.groupId) ?? new Set<string>();
		if (r.visible) set.add(r.elementId);
		else set.delete(r.elementId);
		perGroup.set(r.groupId, set);
	}
	for (const set of perGroup.values()) {
		for (const el of set) grants.add(el);
	}
	return grants;
}

/**
 * Route guard: throws a redirect to /403 when the current user cannot see `elementId`
 * according to the same visibility rules the sidebar UI uses. super_admin bypasses the
 * check entirely — backend endpoints enforce their own super_admin gates (defense in
 * depth), and super_admin is the only role that can restore its own grants if locked out.
 *
 * Semantics mirror `VisibilityProvider.isVisible` (visibility-provider.tsx:157):
 *  1. `elementId` itself must be in the user's union grants (role ∪ org-group).
 *  2. Every registered ancestor of `elementId` (derived from sidebar-config) must
 *     also be granted — otherwise hiding a whole sidebar module (e.g. `sidebar.kartoteka`)
 *     would still let children leak via direct URL.
 *
 * Cache sharing: reuses the exact query keys from `useMyVisibilityRules` /
 * `useMyOrgVisibilityRules`, including the tenant id — pulled from the canonical
 * module-level `getTenantOverride()` so tenant-switched sessions hit the same cache
 * entry as the React provider.
 *
 * Error policy: errors from the rules fetch are rethrown (let TanStack Router's error
 * boundary show the real failure) — the only redirect to /403 is when the rules come
 * back cleanly and `elementId` is genuinely absent. Org-rule fetch failures are
 * tolerated because they are additive-only (mirrors `VisibilityProviderConnected` which
 * falls back to `[] `on failure).
 */
export async function ensureElementVisible(
	queryClient: QueryClient,
	elementId: string,
	from?: string,
) {
	// Embedded (panel agenta): pomijamy frontendowy guard widoczności sidebara —
	// device-JWT nie ma konfiguracji widoczności, a bezpieczeństwo i tak egzekwuje
	// backend (wymuszony scope per apteka). Bez tego /asystent rzucałby 403.
	if (isEmbedded()) return;
	await ensureAuthenticated();
	const user = await loadUser();

	if (user.roles.includes("super_admin")) return;

	// Canonical tenant source is the module-level _tenantOverrideId in auth-api —
	// VisibilityProviderConnected derives selectedTenantId from the same setter via
	// TenantSwitcherProvider, so the cache key below always lines up with the hook.
	const tenantId = getTenantOverride() ?? undefined;

	// Oba zapytania sa niezalezne, wiec leca rownolegle — sekwencyjnie kosztowaly
	// jeden round trip wiecej na kazdym zimnym wejsciu. `Promise.all` tu NIE
	// wystarczy: zapytanie o reguly grup organizacyjnych ma celowy `catch` (patrz
	// nizej), a `Promise.all` odrzucilby cala pare i wyrzucil trase na error
	// boundary zamiast zawezic uprawnienia.
	const [rulesResult, orgResult] = await Promise.allSettled([
		queryClient.ensureQueryData({
			queryKey: VISIBILITY_RULES_QUERY_KEY(tenantId),
			queryFn: () => adminApi.getMyVisibilityRules(),
			staleTime: VISIBILITY_STALE_MS,
		}),
		queryClient.ensureQueryData({
			queryKey: ORG_VISIBILITY_RULES_QUERY_KEY(tenantId),
			queryFn: () => adminApi.getMyOrgVisibilityRules(),
			staleTime: VISIBILITY_STALE_MS,
		}),
	]);

	// Blad regul uzytkownika NIE jest lapany: 401 obsluzyl juz ensureAuthenticated
	// + silentRefresh, a 5xx nalezy do error boundary routera, nie do /403.
	// Zamiana awarii na „Brak dostepu" wysyla usera na zla sciezke naprawy.
	if (rulesResult.status === "rejected") throw rulesResult.reason;
	const rules = rulesResult.value;

	// Reguly grup organizacyjnych tylko dokladaja uprawnienia — ich brak zaweza
	// dostep, nigdy go nie poszerza, wiec odrzucenie tego zapytania jest tolerowane.
	const orgRules: { groupId: number; elementId: string; visible: boolean }[] =
		orgResult.status === "fulfilled" ? (orgResult.value?.data ?? []) : [];

	const grants = computeGrantedElements(rules, orgRules);
	if (!grants.has(elementId)) {
		throw redirect({ to: "/403", search: { element: elementId, from } });
	}
	if (!isParentGranted(elementId, grants, sidebarRegisteredElements())) {
		throw redirect({ to: "/403", search: { element: elementId, from } });
	}
}
